import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Trash2, Reply, Pencil } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import { logWarn } from "@/lib/logger";

function EditContactDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    email: item?.email || "",
    subject: item?.subject || "",
    message: item?.message || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/contact-submissions/${item.id}`, form);
      toast.success("Contatto aggiornato");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E5E3] max-w-lg" data-testid="admin-contact-edit-dialog" aria-describedby="admin-contact-edit-desc">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Modifica contatto
          </DialogTitle>
        </DialogHeader>
        <div id="admin-contact-edit-desc" className="space-y-3 py-2">
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                 placeholder="Nome" data-testid="admin-contact-edit-name" />
          <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                 placeholder="Email" type="email" data-testid="admin-contact-edit-email" />
          <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                 placeholder="Oggetto" data-testid="admin-contact-edit-subject" />
          <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    rows={5} placeholder="Messaggio" data-testid="admin-contact-edit-message" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#6B6B6B]">Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-contact-edit-save"
                  className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
            {busy ? "…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ContactInbox() {
  const { lang } = useApp();
  const [items, setItems] = useState([]);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/contact-submissions");
      setItems(data);
    } catch (e) { logWarn("contact inbox load failed", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try { await api.post(`/admin/contact-submissions/${id}/read`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete_message", lang))) return;
    try { await api.delete(`/admin/contact-submissions/${id}`); toast.success("Messaggio eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!items.length) {
    return <p className="text-sm text-[#6B6B6B] py-8 text-center" data-testid="admin-contact-empty">Nessun messaggio ricevuto dal form contatti.</p>;
  }
  return (
    <div className="space-y-3" data-testid="admin-contact-list">
      {items.map((s) => (
        <div key={s.id} data-testid={`admin-contact-${s.id}`} className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-4">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              {!s.read && <span className="inline-block h-2 w-2 rounded-full bg-[#B8965A]" aria-label="Non letto" />}
              <p className="text-sm font-medium text-[#1A1A1A] truncate">{s.name}</p>
              <a href={`mailto:${s.email}?subject=${encodeURIComponent("Re: " + (s.subject || "Contatto"))}`}
                 className="text-xs font-mono text-[#B8965A] hover:underline">{s.email}</a>
            </div>
            <div className="flex gap-1.5">
              {!s.read && (
                <Button size="sm" onClick={() => markRead(s.id)} data-testid={`admin-contact-read-${s.id}`}
                        className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full text-xs h-8">
                  <Mail className="h-3.5 w-3.5 me-1" /> Letto
                </Button>
              )}
              <Button size="sm" onClick={() => setEditItem(s)} data-testid={`admin-contact-edit-${s.id}`}
                      className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full text-xs h-8">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" asChild data-testid={`admin-contact-reply-${s.id}`}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-xs h-8">
                <a href={`mailto:${s.email}?subject=${encodeURIComponent("Re: " + (s.subject || "Contatto"))}`}>
                  <Reply className="h-3.5 w-3.5 me-1" /> Rispondi
                </a>
              </Button>
              <Button size="sm" onClick={() => remove(s.id)} data-testid={`admin-contact-delete-${s.id}`}
                      className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full text-xs h-8">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-[#6B6B6B] mb-2">
            {s.subject || "—"} · <span className="font-mono">{new Date(s.created_at).toLocaleString()}</span>
            {s.email_failed && <Badge className="ms-2 bg-red-100 text-red-800 border border-red-200 text-[10px]">EMAIL FAILED</Badge>}
          </p>
          <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap break-words">{s.message}</p>
        </div>
      ))}
      {editItem && (
        <EditContactDialog
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); load(); }}
        />
      )}
    </div>
  );
}
